import 'css/src/components/common/Header';
import 'css/src/components/dashboard/DashboardHeader';

import React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import classNames from 'classnames';

import { HelpDropdown } from './HelpDropdown';
import { SubmitFeedbackModal } from './SubmitFeedbackModal';

import { Urls } from 'js/src/client';
import HeaderLogo from 'js/src/components/common/HeaderLogo';
import NotificationDrawer, {
    NotificationDrawerToggle,
} from 'js/src/components/common/Notifications';
import {
    BUTTON_SIZES,
    SecondaryButton,
    TertiaryIconButton,
} from 'js/src/components/common/ui/Button';
import UserOptionsDropdown from 'js/src/components/common/UserOptionsDropdown';
import VideoSearch from 'js/src/components/dashboard/VideoSearch';
import { loadTeamMembers, loadUserProfile, loadWorkspaces } from 'js/src/data/account';
import { selectDashboardName } from 'js/src/data/account/account-selectors';
import { trackUpsellClicked } from 'js/src/libs/analytics-utils';
import type { AppDispatch, RootState } from 'js/src/libs/RootState';

type PropsFromRedux = ConnectedProps<typeof connector>;

type OwnProps = {
    hasSidebar: boolean;
};

const DashboardHeaderPresentation = React.memo(function DashboardHeaderPresentation({
    hasSidebar = true,
    username,
    photoUrl,
    role,
    loadWorkspaces,
    loadUserProfile,
    loadTeamMembers,
}: PropsFromRedux & OwnProps) {
    const [drawerShowing, setDrawerShowing] = React.useState(false);

    const upgradeClick = React.useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        window.open(Urls.PRICING);
        trackUpsellClicked('dashboard_header');
    }, []);

    const toggleMobileSidebar = React.useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        document.body.classList.toggle('sidebar-mobile-main');
    }, []);

    const windowResize = React.useCallback(() => {
        if (window.innerWidth > 768 && document.body) {
            document.body.classList.remove('sidebar-mobile-main');
        }
    }, []);

    const toggleDrawer = React.useCallback((show: boolean | null = null) => {
        // if no args are passed, toggle the state
        if (show === null) {
            setDrawerShowing((prev) => !prev);
        }
        // otherwise, explicitly control the <Drawer/>
        else {
            setDrawerShowing(show);
        }
    }, []);

    const hideDrawer = React.useCallback(() => {
        toggleDrawer(false);
    }, [toggleDrawer]);

    React.useEffect(() => {
        if (SERVER.USER !== null) {
            // Only load info if a user is logged in
            loadWorkspaces();
            loadUserProfile();
            loadTeamMembers();
        }

        window.addEventListener('resize', windowResize);

        return () => {
            window.removeEventListener('resize', windowResize);
        };
    }, [loadTeamMembers, loadUserProfile, loadWorkspaces, windowResize]);

    const showUpgradeButton = SERVER.USER !== null && SERVER.USER.plan === '';

    return (
        <div
            className={classNames('header dashboard-header', {
                'border-bottom': !hasSidebar,
            })}
        >
            {/* mobile header */}
            <div className="d-flex header-left d-md-none">
                <HeaderLogo />
            </div>
            <div className="d-flex header-right d-md-none">
                <HelpDropdown />
                <UserOptionsDropdown role={role} username={username} photoUrl={photoUrl} />
                {hasSidebar && (
                    <>
                        <div className="vertical_line mx-l5-12" />
                        <TertiaryIconButton
                            icon="menu"
                            onClick={toggleMobileSidebar}
                            className="mx-l5-12"
                        />
                    </>
                )}
            </div>

            {/* desktop header */}
            <div className="header-left d-none d-md-flex">
                {/* typically nothing, but required for spacing */}
                {!hasSidebar && <HeaderLogo />}
            </div>
            <div className="header-center d-none d-md-flex">
                {window.location.pathname === (Urls.DASHBOARD as string) && <VideoSearch />}
            </div>
            <div className="header-right d-none d-md-flex">
                {showUpgradeButton && (
                    <SecondaryButton
                        onClick={upgradeClick}
                        size={BUTTON_SIZES.SM}
                        text="Upgrade"
                        className="mx-l5-8"
                    />
                )}
                {SERVER.USER && (
                    <>
                        <div className="me-l5-8">
                            <NotificationDrawerToggle onClick={toggleDrawer} />
                        </div>
                        <HelpDropdown />
                        <UserOptionsDropdown role={role} username={username} photoUrl={photoUrl} />
                    </>
                )}
            </div>
            {SERVER.USER && <NotificationDrawer show={drawerShowing} onHide={hideDrawer} />}
            <SubmitFeedbackModal key="submit_feedback_modal" />
        </div>
    );
});

const mapStateToProps = (state: RootState) => {
    return {
        username: selectDashboardName(state),
        photoUrl: state.account.user_profile.photo_url,
        role: state.account.user_profile.role,
    };
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
    return bindActionCreators(
        {
            loadWorkspaces,
            loadUserProfile,
            loadTeamMembers,
        },
        dispatch,
    );
};

const connector = connect(mapStateToProps, mapDispatchToProps);

const DashboardHeader = connector(DashboardHeaderPresentation);

export default DashboardHeader;
